import { RootState, useAppDispatch } from '_store';
import applicationReducer from '_store/application/reducer';
import { DialogType } from '_store/application/types';
import pointFormReducer from '_store/forms/create-point';
import denmFormReducer from '_store/forms/manage-denm';
import ivimFormReducer from '_store/forms/manage-ivim';
import managePmvFormReducer from '_store/forms/manage-pmv';
import manageRsuFormReducer from '_store/forms/manage-rsu';
import manageSpatemRsusFormReducer from '_store/forms/manage-spatem-rsus';
import { useLocalization } from '@fluent/react';
import { Box, Card, IconButton, Tooltip } from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import ChevronRight from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { usePmvL10n } from '../../../hooks/usePmvL10n';
import { useRolePermissions } from '../../../hooks/useRolePermissions';
import MessageDialog from '../../items/MessageDialog';
import AuditListLayout from '../../scenes/Audit/AuditList';
import CamListLayout from '../../scenes/Cam/CamList';
import Dashboard from '../../scenes/Dashboard/Dashboard';
import CreateDenmLayout from '../../scenes/Denm/CreateDenm';
import DenmListLayout from '../../scenes/Denm/DenmList';
import { DenmTypeEnum } from '../../scenes/Denm/types';
import DeviceLayout from '../../scenes/Device';
import DeviceLayoutSingle from '../../scenes/Device/single';
import CamLayout from '../../scenes/Event/Cam';
import DenmLayout from '../../scenes/Event/Denm';
import IvimLayout from '../../scenes/Event/Ivim';
import HomeLayout from '../../scenes/Home';
import IvimCrudLayout from "../../scenes/Ivim";
import IvimListLayout from "../../scenes/Ivim/IvimList";
import { IvimTypeEnum } from "../../scenes/Ivim/types";
import PmvListLayout from '../../scenes/Pmv';
import PmvCrudLayout from '../../scenes/Pmv/layout';
import PmvLayoutSingle from '../../scenes/Pmv/single';
import { PmvCrudTypeEnum } from '../../scenes/Pmv/types';
import RsuCrudLayout from '../../scenes/Rsu';
import { CrudTypeEnum } from '../../scenes/Rsu/types';
import SettingsLayout from '../../scenes/Settings';
import SpatemLayout from '../../scenes/Spatem';
import SpatemCrudLayout from '../../scenes/Spatem/layout';
import SpatemLayoutSingle from '../../scenes/Spatem/single';
import { SpatemCrudTypeEnum } from '../../scenes/Spatem/types';
import { toLatLng } from '../MapView/utils';
import { CardHeaderBox } from './CardHeaderBox';
import styles from './styles';
import { canInfoViewBeClosed } from './utils';

enum EntryTypeType {
  NEW_POINTS = 'newPoints',
  DELETE_POINTS = 'deletePoints',
  EXTEND_POINTS = 'extendPoints',
}

const InfoView: FC = () => {
  const isClosable = canInfoViewBeClosed();
  const { l10n } = useLocalization();
  const { getPmvString } = usePmvL10n();
  const classes = styles();
  const dispatch = useAppDispatch();
  const {
    canWriteDenm,
    canWriteIvim,
    canManageRsu,
    canManagePmv,
    canManageSpatem,
  } = useRolePermissions();

  const dialog = useSelector((state: RootState) => state.application.activeDialog);
  const mapRef = useSelector((state: RootState) => state.application.mapRef);

  const [entryType, setEntryType] = useState<{
    type: EntryTypeType | CrudTypeEnum | DenmTypeEnum | IvimTypeEnum | PmvCrudTypeEnum | SpatemCrudTypeEnum;
    id: string;
  }>({
    type: null,
    id: null,
  });

  const [showMessage, setShowMessage] = useState({
    active: false,
    typeAction: '',
  });

  const [isFullWidth, setIsFullWidth] = useState(false);
  const [heatmapPanelCollapsed, setHeatmapPanelCollapsed] = useState(false);
  const [panelRightEdge, setPanelRightEdge] = useState<number | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const updatePanelRightEdge = () => {
    if (dialog?.type === DialogType.DASHBOARD && cardRef.current) {
      const rect = cardRef.current.getBoundingClientRect();
      setPanelRightEdge(rect.right);
    } else {
      setPanelRightEdge(null);
    }
  };

  useLayoutEffect(() => {
    updatePanelRightEdge();
    const onResize = () => updatePanelRightEdge();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [dialog?.type, heatmapPanelCollapsed, isFullWidth]);

  useEffect(() => {
    if (
      (entryType.type !== null &&
        dialog.type !== DialogType.DEVICE &&
        entryType.type === CrudTypeEnum.NEW_RSU) ||
      (entryType.type !== null &&
        dialog.type !== DialogType.PMV_LIST &&
        (entryType.type === PmvCrudTypeEnum.NEW_PMV ||
          entryType.type === PmvCrudTypeEnum.UPDATE_PMV)) ||
      (entryType.type === SpatemCrudTypeEnum.UPDATE_SPATEM_RSUS &&
        dialog.type !== DialogType.SPATEM_LIST &&
        dialog.type !== DialogType.SINGLE_SPATEM) ||
      (dialog.type !== DialogType.DENM_LIST &&
        dialog.type !== DialogType.UPDATE_DENM &&
        (entryType.type === DenmTypeEnum.NEW_DENM ||
          entryType.type === DenmTypeEnum.UPDATE_DENM ||
          entryType.type === DenmTypeEnum.CLONE_DENM)) ||
      (dialog.type !== DialogType.IVIM_LIST &&
        dialog.type !== DialogType.UPDATE_IVIM &&
        (entryType.type === IvimTypeEnum.NEW_IVIM ||
          entryType.type === IvimTypeEnum.UPDATE_IVIM ||
          entryType.type === IvimTypeEnum.CLONE_IVIM))
    ) {
      setShowMessage({ active: true, typeAction: 'sideButton' });
    }
  }, [dialog]);

  const closeInfoView = () => {
    setShowMessage({ active: true, typeAction: 'closeButton' });
  };

  const onGoingBack = () => {
    setShowMessage({ active: true, typeAction: 'backButton' });
  };

  const closeDialog = () => {
    setEntryType({ type: null, id: null });
  };

  const goToDenmList = () => {
    dispatch(applicationReducer.actions.infoSelected({ type: DialogType.DENM_LIST, id: '1' }));
  };

  const goToIvimList = () => {
    dispatch(applicationReducer.actions.infoSelected({ type: DialogType.IVIM_LIST, id: '2' }));
  };

  const onMessageLeaveConfirmed = (type: string) => {
    if (type === 'confirm') {
      if (['backButton', 'sideButton'].includes(showMessage.typeAction)) {
        setEntryType({ type: null, id: null });
        if (
          [
            EntryTypeType.NEW_POINTS,
            EntryTypeType.DELETE_POINTS,
            EntryTypeType.EXTEND_POINTS,
            CrudTypeEnum.NEW_RSU,
            CrudTypeEnum.UPDATE_RSU,
            PmvCrudTypeEnum.NEW_PMV,
            PmvCrudTypeEnum.UPDATE_PMV,
            DenmTypeEnum.UPDATE_DENM,
            DenmTypeEnum.CLONE_DENM,
            IvimTypeEnum.UPDATE_IVIM,
            IvimTypeEnum.CLONE_IVIM,
          ].includes(entryType.type as CrudTypeEnum | EntryTypeType | DenmTypeEnum | IvimTypeEnum)
        ) {
          dispatch(applicationReducer.actions.manageSelectionAborted());
          dispatch(applicationReducer.actions.pointSelectionAborted());
          dispatch(applicationReducer.actions.deletePointsAborted());
        }
      } else if (entryType.type !== EntryTypeType.DELETE_POINTS) {
        dispatch(applicationReducer.actions.dialogClosed());
      } else {
        dispatch(applicationReducer.actions.dialogClosed());
        dispatch(applicationReducer.actions.deletePointsAborted());
        dispatch(applicationReducer.actions.pointSelectionAborted());
      }
    } else if (entryType.type === CrudTypeEnum.NEW_RSU) {
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.DEVICE,
          id: '3',
        }),
      );
    } else if (
      entryType.type === PmvCrudTypeEnum.NEW_PMV ||
      entryType.type === PmvCrudTypeEnum.UPDATE_PMV
    ) {
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.PMV_LIST,
          id: 'pmv-list',
        }),
      );
    } else if (
      entryType.type === SpatemCrudTypeEnum.UPDATE_SPATEM_RSUS &&
      dialog?.type !== DialogType.SPATEM_LIST &&
      dialog?.type !== DialogType.SINGLE_SPATEM
    ) {
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.SPATEM_LIST,
          id: '4',
        }),
      );
    } else if (
      entryType.type === DenmTypeEnum.NEW_DENM ||
      entryType.type === DenmTypeEnum.UPDATE_DENM ||
      entryType.type === DenmTypeEnum.CLONE_DENM
    ) {
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.DENM_LIST,
          id: '1',
        }),
      );
    } else if (
      entryType.type === IvimTypeEnum.NEW_IVIM ||
      entryType.type === IvimTypeEnum.UPDATE_IVIM ||
      entryType.type === IvimTypeEnum.CLONE_IVIM
    ) {
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.IVIM_LIST,
          id: '2',
        }),
      );
    }

    setShowMessage((sm) => ({ ...sm, active: false }));
  };

  const closePage = () => {
    const returnTo = dialog?.returnTo;

    dispatch(applicationReducer.actions.dialogDismissed());

    if (returnTo?.coordinates && mapRef) {
      mapRef.flyTo(toLatLng(returnTo.coordinates), returnTo.zoom ?? 15, {
        animate: true,
        duration: 1,
      });
    }

    if (!returnTo) {
      dispatch(denmFormReducer.actions.resetForm());
      dispatch(ivimFormReducer.actions.resetForm());
      dispatch(pointFormReducer.actions.resetForm());
      dispatch(manageRsuFormReducer.actions.resetForm());
      dispatch(managePmvFormReducer.actions.resetForm());
      dispatch(manageSpatemRsusFormReducer.actions.resetForm());
      dispatch(applicationReducer.actions.pointSelectionAborted());
      dispatch(applicationReducer.actions.manageSelectionAborted());
    }
  };

  const goBackFromEvent = () => {
    if (dialog?.returnTo) {
      closePage();
      return;
    }

    if (dialog.type === DialogType.DENM) {
      goToDenmList();
      return;
    }

    goToIvimList();
  };

  const onCloseInfoView = () => {
    setShowMessage({ active: true, typeAction: dialog.type });
  };

  const messageInfoView = (type: string) => {
    if (type === 'confirm') {
      closePage();
    }

    setShowMessage((sm) => ({ ...sm, active: false }));
  };

  const toggleFullWidth = () => {
    setIsFullWidth(!isFullWidth);
  };

  if (entryType.type === null) {
    const createNewRsu = () => {
      if (!canManageRsu) return;
      setEntryType({ ...entryType, type: CrudTypeEnum.NEW_RSU });
    };

    const updateRsu = (idExpanded) => {
      if (!canManageRsu) return;
      setEntryType({ type: CrudTypeEnum.UPDATE_RSU, id: idExpanded });
    };

    const createNewPmv = () => {
      if (!canManagePmv) return;
      setEntryType({ ...entryType, type: PmvCrudTypeEnum.NEW_PMV });
    };

    const updatePmv = (idExpanded) => {
      if (!canManagePmv) return;
      setEntryType({ type: PmvCrudTypeEnum.UPDATE_PMV, id: idExpanded });
    };

    const updateSpatem = (idExpanded) => {
      if (!canManageSpatem) return;
      setEntryType({ type: SpatemCrudTypeEnum.UPDATE_SPATEM_RSUS, id: idExpanded });
    };

    const updateDenm = (idExpanded) => {
      if (!canWriteDenm) return;
      setEntryType({ type: DenmTypeEnum.UPDATE_DENM, id: idExpanded });
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.DENM_LIST,
          id: idExpanded,
        }),
      );
    };

    const cloneDenm = (idExpanded) => {
      if (!canWriteDenm) return;
      setEntryType({ type: DenmTypeEnum.CLONE_DENM, id: idExpanded });
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.DENM_LIST,
          id: idExpanded,
        }),
      );
    };

    const createNewDenm = () => {
      if (!canWriteDenm) return;
      setEntryType({ ...entryType, type: DenmTypeEnum.NEW_DENM });
    };

    const updateIvim = (idExpanded) => {
      if (!canWriteIvim) return;
      setEntryType({ type: IvimTypeEnum.UPDATE_IVIM, id: idExpanded });
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.IVIM_LIST,
          id: idExpanded,
        }),
      );
    };

    const cloneIvim = (idExpanded) => {
      if (!canWriteIvim) return;
      setEntryType({ type: IvimTypeEnum.CLONE_IVIM, id: idExpanded });
      dispatch(
        applicationReducer.actions.infoSelected({
          type: DialogType.IVIM_LIST,
          id: idExpanded,
        }),
      );
    };

    const createNewIvim = () => {
      if (!canWriteIvim) return;
      setEntryType({ ...entryType, type: IvimTypeEnum.NEW_IVIM });
    };

    let LayoutType = null;
    let CardHeaderTitle = null;

    const dialogActions = {
      [DialogType.DENM_LIST]: {
        action: canWriteDenm ? createNewDenm : undefined,
        text: l10n.getString('forms-denm-add-new'),
      },
      [DialogType.DENM_PATH]: {
        action: canWriteDenm ? createNewDenm : undefined,
        text: l10n.getString('forms-denm-add-new'),
      },
      [DialogType.IVIM_LIST]: {
        action: canWriteIvim ? createNewIvim : undefined,
        text: l10n.getString('forms-ivim-add-new'),
      },
      [DialogType.IVIM_PATH]: {
        action: canWriteIvim ? createNewIvim : undefined,
        text: l10n.getString('forms-ivim-add-new'),
      },
      [DialogType.DEVICE]: {
        action: canManageRsu ? createNewRsu : undefined,
        text: l10n.getString('forms-rsu-add-new'),
      },
      [DialogType.DEVICE_PATH]: {
        action: canManageRsu ? createNewRsu : undefined,
        text: l10n.getString('forms-rsu-add-new'),
      },
      [DialogType.PMV_LIST]: {
        action: canManagePmv ? createNewPmv : undefined,
        text: getPmvString('forms-pmv-add-new'),
      },
      [DialogType.PMV_PATH]: {
        action: canManagePmv ? createNewPmv : undefined,
        text: getPmvString('forms-pmv-add-new'),
      },
    };

    switch (dialog.type) {
      case DialogType.DENM:
        LayoutType = (
          <DenmLayout
            id={dialog.id}
            onUpdateDenm={canWriteDenm ? (idExpanded) => updateDenm(idExpanded) : undefined}
            onCloneDenm={canWriteDenm ? (idExpanded) => cloneDenm(idExpanded) : undefined}
          />
        );
        break;
      case DialogType.DENM_LIST:
      case DialogType.DENM_PATH:
        CardHeaderTitle = l10n.getString('forms-denm-list');
        LayoutType = (
          <DenmListLayout
            onUpdateDenm={canWriteDenm ? (idExpanded) => updateDenm(idExpanded) : undefined}
            onCloneDenm={canWriteDenm ? (idExpanded) => cloneDenm(idExpanded) : undefined}
          />
        );
        break;
      case DialogType.IVIM_LIST:
      case DialogType.IVIM_PATH:
        CardHeaderTitle = l10n.getString('forms-ivim-list');
        LayoutType = (
          <IvimListLayout
            onUpdateIvim={canWriteIvim ? (idExpanded) => updateIvim(idExpanded) : undefined}
            onCloneIvim={canWriteIvim ? (idExpanded) => cloneIvim(idExpanded) : undefined}
          />
        );
        break;

      case DialogType.IVIM:
        LayoutType = (
          <IvimLayout
            id={dialog.id}
            onUpdateIvim={canWriteIvim ? (idExpanded) => updateIvim(idExpanded) : undefined}
            onCloneIvim={canWriteIvim ? (idExpanded) => cloneIvim(idExpanded) : undefined}
          />
        );
        break;
      case DialogType.DEVICE:
      case DialogType.DEVICE_PATH:
        CardHeaderTitle = l10n.getString('forms-rsu-list');

        LayoutType = (
          <DeviceLayout
            onUpdateRsu={canManageRsu ? (idExpanded) => updateRsu(idExpanded) : undefined}
          />
        );
        break;
      case DialogType.SINGLE_DEVICE:
        LayoutType = (
          <DeviceLayoutSingle
            id={dialog.id}
            onUpdateRsu={canManageRsu ? (idExpanded) => updateRsu(idExpanded) : undefined}
          />
        );
        break;
      case DialogType.PMV_LIST:
      case DialogType.PMV_PATH:
        CardHeaderTitle = getPmvString('forms-pmv-list');
        LayoutType = (
          <PmvListLayout
            onUpdatePmv={canManagePmv ? (idExpanded) => updatePmv(idExpanded) : undefined}
          />
        );
        break;
      case DialogType.SINGLE_PMV:
        LayoutType = (
          <PmvLayoutSingle
            id={dialog.id}
            onUpdatePmv={canManagePmv ? (idExpanded) => updatePmv(idExpanded) : undefined}
          />
        );
        break;
      case DialogType.SETTINGS:
        CardHeaderTitle = l10n.getString('forms-settings');
        LayoutType = <SettingsLayout />;
        break;
      case DialogType.CAM:
        LayoutType = <CamLayout id={dialog.id} />;
        break;
      case DialogType.CAM_LIST:
        CardHeaderTitle = l10n.getString('forms-cam-list');
        LayoutType = <CamListLayout />;
        break;
      case DialogType.SPATEM_LIST:
        CardHeaderTitle = l10n.getString('forms-spatem-list');
        LayoutType = (
          <SpatemLayout
            onUpdateSpatem={canManageSpatem ? (idExpanded) => updateSpatem(idExpanded) : undefined}
          />
        );
        break;
      case DialogType.SINGLE_SPATEM:
        CardHeaderTitle = l10n.getString('forms-spatem-detail-header');
        LayoutType = (
          <SpatemLayoutSingle
            id={dialog.id}
            onUpdateSpatem={canManageSpatem ? (idExpanded) => updateSpatem(idExpanded) : undefined}
          />
        );
        break;
      case DialogType.AUDIT_LIST:
        CardHeaderTitle = l10n.getString('audit-title');
        LayoutType = <AuditListLayout />;
        break;
      case DialogType.DASHBOARD:
        LayoutType = (
          <Dashboard
            heatmapPanelCollapsed={heatmapPanelCollapsed}
            onHeatmapCollapseToggle={() => setHeatmapPanelCollapsed((c) => !c)}
            panelRightEdge={panelRightEdge}
          />
        );
        break;
      default:
        LayoutType = <HomeLayout />;
    }

    const { action, text } = dialogActions[dialog.type] || {};

    const dashboardCardClass = [
      isFullWidth ? classes.cardStyleDashboardFullWidth : classes.cardStyleDashboard,
      heatmapPanelCollapsed && classes.cardStyleDashboardCollapsed,
    ]
      .filter(Boolean)
      .join(' ');

    // the audit table reuses the wide card: the rail is far too narrow for a six-column table
    const wideCardTypes = [DialogType.DASHBOARD, DialogType.AUDIT_LIST];
    let cardClassName = classes.cardStyle;
    if (dialog?.type === DialogType.DASHBOARD) {
      cardClassName = dashboardCardClass;
    } else if (wideCardTypes.includes(dialog?.type)) {
      cardClassName = classes.cardStyleDashboard;
    }

    return (
      <Card
        ref={dialog?.type === DialogType.DASHBOARD ? cardRef : undefined}
        className={cardClassName}
      >
        <Box className={classes.icons}>
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            {[DialogType.DENM, DialogType.IVIM].includes(dialog.type) && (
              <Tooltip title={l10n.getString('forms-event-go-to-the-list')} placement="right">
                <IconButton
                  className={classes.backIcon}
                  onClick={goBackFromEvent}
                >
                  <ArrowBack />
                </IconButton>
              </Tooltip>
            )}
            {dialog.type === DialogType.DASHBOARD && (
              <IconButton className={classes.backIcon} onClick={toggleFullWidth}>
                {isFullWidth ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            )}
          </Box>
          <IconButton className={classes.close} onClick={isClosable ? onCloseInfoView : closePage}>
            <CloseIcon />
          </IconButton>
        </Box>

        {showMessage.active ? (
          <MessageDialog statusMessage={showMessage} onCloseDialog={messageInfoView} />
        ) : null}

        <CardHeaderBox
          cardHeaderTitle={CardHeaderTitle}
          onFabClick={action}
          onFabClickText={text}
        />

        {LayoutType}
        {dialog?.type === DialogType.DASHBOARD && heatmapPanelCollapsed && (
          <Tooltip title={l10n.getString('dashboard-heatmap-expand-panel') ?? 'Expand panel'} placement="left">
            <IconButton
              className={classes.heatmapPanelToggle}
              style={panelRightEdge != null ? { left: panelRightEdge + 4, right: 'auto' } : undefined}
              onClick={() => setHeatmapPanelCollapsed(false)}
              size="small"
              aria-label="Expand heatmap panel"
            >
              <ChevronRight />
            </IconButton>
          </Tooltip>
        )}
      </Card>
    );
  }
  return (
    <>
      {[CrudTypeEnum.NEW_RSU, CrudTypeEnum.UPDATE_RSU].includes(entryType.type as CrudTypeEnum) && (
        <Card className={classes.cardStyle}>
          <RsuCrudLayout
            id={entryType.id}
            type={entryType.type as CrudTypeEnum}
            onClose={closeInfoView}
            onGoingBack={onGoingBack}
          />
        </Card>
      )}

      {[PmvCrudTypeEnum.NEW_PMV, PmvCrudTypeEnum.UPDATE_PMV].includes(
        entryType.type as PmvCrudTypeEnum,
      ) && (
        <Card className={classes.cardStyle}>
          <PmvCrudLayout
            id={entryType.id}
            type={entryType.type as PmvCrudTypeEnum}
            onClose={closeInfoView}
            onGoingBack={onGoingBack}
          />
        </Card>
      )}

      {entryType.type === SpatemCrudTypeEnum.UPDATE_SPATEM_RSUS && (
        <Card className={classes.cardStyle}>
          <SpatemCrudLayout
            id={entryType.id}
            onClose={closeInfoView}
            onGoingBack={onGoingBack}
            closeDialog={closeDialog}
          />
        </Card>
      )}

      {[DenmTypeEnum.NEW_DENM, DenmTypeEnum.UPDATE_DENM, DenmTypeEnum.CLONE_DENM].includes(
        entryType.type as DenmTypeEnum,
      ) && (
        <Card className={classes.cardStyle}>
          <CreateDenmLayout
            id={entryType.id}
            type={entryType.type as DenmTypeEnum}
            onClose={closeInfoView}
            onGoingBack={onGoingBack}
            closeDialog={closeDialog}
          />
        </Card>
      )}

      {[IvimTypeEnum.NEW_IVIM, IvimTypeEnum.UPDATE_IVIM, IvimTypeEnum.CLONE_IVIM].includes(
        entryType.type as IvimTypeEnum,
      ) && (
        <Card className={classes.cardStyle}>
          <IvimCrudLayout
            id={entryType.id}
            type={entryType.type as IvimTypeEnum}
            onClose={closeInfoView}
            onGoingBack={onGoingBack}
            closeDialog={closeDialog}
          />
        </Card>
      )}

      {showMessage.active && (
        <MessageDialog statusMessage={showMessage} onCloseDialog={onMessageLeaveConfirmed} />
      )}
    </>
  );
};

export default React.memo(InfoView);
